import { useTheme } from "@hooks/useTheme";
import { useEffect, useState } from "react";
import queryKeys from "@configs/react-query/query-key";
import { v4 as uuidv4 } from "uuid";

import { ClaritySC } from "@constants/sbom";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import Breadcrumb from "@components/UI/Breadcrumb";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomSelect, DualSelectBox } from "@components/UI";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { extractErrorCode } from "@utils/error";
import { CustomTextarea } from "@components/Forms";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";


const AddSbom = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedAvailableItems, setSelectedAvailableItems] = useState([]);
  const [selectedSelectedItems, setSelectedSelectedItems] = useState([]);

  const {
    data: codesData,
    isLoading: isCodesLoading,
    error: codesError,
  } = useFetchData(
    [queryKeys.getCodeListForSbom],
    queryKeys.getCodeListForSbom,
    {
      method: "GET",
    },
    {},
    {},
  );

  const breadcrumbItems = [
    { label: t("sbom.management-title"), href: "/sbom" },
    { label: t("sbom.add-new-sbom"), href: "/sbom/new" },
  ];

  const [formData, setFormData] = useState({
    sbomName: "",
    sbomTypeCd: ClaritySC,
    sbomItems: [],
    sbomSeq: -2,
    remark: "",
  })

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // DualSelectbox handlers
  const handleMoveToSelected = () => {
    if (selectedAvailableItems.length === 0) return;

    const newSelected = [...selectedItems, ...selectedAvailableItems];
    setSelectedItems(newSelected);

    // Remove moved items from available items
    const updatedAvailable = availableItems.filter(
      (item) => !selectedAvailableItems.some((selected) => selected.value === item.value),
    );
    setAvailableItems(updatedAvailable);

    // Clear selection
    setSelectedAvailableItems([]);
  };

  const handleMoveToAvailable = () => {
    if (selectedSelectedItems.length === 0) return;

    // Move selected items to the top of available items
    const newAvailable = [...selectedSelectedItems, ...availableItems];
    setAvailableItems(newAvailable);

    // Remove moved items from selected items
    const updatedSelected = selectedItems.filter(
      (item) => !selectedSelectedItems.some((selected) => selected.value === item.value),
    );
    setSelectedItems(updatedSelected);

    // Clear selection
    setSelectedSelectedItems([]);
  };

  const handleSelectAllAvailable = () => {
    if (availableItems.length === 0) return;

    // If all items are already selected, deselect all
    if (selectedAvailableItems.length === availableItems.length) {
      setSelectedAvailableItems([]);
    } else {
      // Otherwise, select all items
      setSelectedAvailableItems([...availableItems]);
    }
  };
  
  const handleSelectAllSelected = () => {
    if (selectedItems.length === 0) return;

    // If all items are already selected, deselect all
    if (selectedSelectedItems.length === selectedItems.length) {
      setSelectedSelectedItems([]);
    } else {
      // Otherwise, select all items
      setSelectedSelectedItems([...selectedItems]);
    }
  };

  const validateForm = () => {
    return (
      formData.sbomName?.trim() &&
      formData.sbomTypeCd?.trim() &&
      selectedItems.length > 0
    );
  };

  const isFormValid = () => {
    return validateForm();
  };

  const createSbom = useMutationData(
    queryKeys.updateSbomList,
    { method: "PUT" },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.SBOMS, response.data, response.data.sbomSeq);
        useSuccessToast(TOAST.success, t("common.success"), t("sbom.create-success"));
        setLoading(false);
        navigate("/sbom");
      },
      onError: (error) => {
        console.error("Error during create sbom:", error);
        const errorCode = extractErrorCode(error, "sbom.create-error");
        useSuccessToast(TOAST.error, t("common.error"), t(errorCode));
        setLoading(false);
      },
    },
  );

  const handleSave = async () => { 
    if (!validateForm()) {
      return;
    }
    
    const dataToSave = [];
    const newSbomItems = selectedItems.map((item) => ({
      sbomItemSeq: -2,
      sbomSeq: -2,
      item: item.value.split("#")[0],
      itemName: item.label,
      verificationTool: item.value.split("#")[1] || "",
      db: false,
      delete: false,
    }));

    dataToSave.push({
      id: uuidv4(),
      createdAt: null,
      db: false,
      delete: false,
      remark: formData.remark?.trim() || "",
      sbomItems: newSbomItems,
      sbomName: formData.sbomName.trim(),
      sbomTypeCd: formData.sbomTypeCd,
      sbomSeq: -2,
    });

    await createSbom.mutateAsync(dataToSave);
  };

  const handleCancel = () => {
    navigate("/sbom");
  };
  
  useEffect(() => {
    if (codesData && !isCodesLoading && !codesError) {
      const mappedAvailable = codesData.map((item) => ({
        value: `${item.code}#${item.codeValue}`,
        label: `${item.codeName} <${item.codeValue}>`,
      }));
      setAvailableItems(mappedAvailable);
    } else if (codesError) {
      useSuccessToast(TOAST.error, t("common.error"), t("sbom.fetch-error"));
    }
  }, [codesData, isCodesLoading, codesError]);

  return (
    <div data-testid="add-sbom-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`}>
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className={`${colors.textPrimary} text-2xl font-bold uppercase`}>
              {t("sbom.add-new-sbom")}
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <OutlineButton data-testid="add-sbom-cancel-button" onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
            <PrimaryButton data-testid="add-sbom-save-button" onClick={handleSave} disabled={!isFormValid() || loading}>
              {loading ? (
                <div className="flex items-center">
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  {t("common.saving")}
                </div>
              ) : (
                t("common.save-button")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div data-testid="add-sbom-form" className={`mt-3 p-6 ${colors.bgPrimary} rounded-2xl`}>
        <div className={`mx-auto max-w-[55rem]`}>
          <div>
            {/* Sbom Information Section */}
            <div className="mb-6">
              <h2 className={`${colors.textPrimary} text-xl font-semibold uppercase`}>
                {t("sbom.sbom-information-title")}
              </h2>
              <p className={`mb-2 ${colors.textSubTitle} text-sm font-normal`}>
                {t("system.required-fields-note")} <span className={colors.textRequired}>*</span>
              </p>

              <div className="grid grid-cols-3 gap-6">
                {/* Sbom Name */}
                <div className="col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("sbom.sbom-name-label")} <span className={colors.textRequired}>*</span>
                  </label>
                  <input
                    data-testid="add-sbom-name-input"
                    type="text"
                    placeholder={t("sbom.sbom-name-placeholder")}
                    value={formData.sbomName}
                    onChange={(e) => handleInputChange("sbomName", e.target.value)}
                    title={formData.sbomName}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                </div>

                {/* Sbom Type */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("sbom.sbom-type")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomSelect
                    value={{ value: ClaritySC, label: ClaritySC }}
                    onChange={() => {}}
                    options={[{ value: ClaritySC, label: ClaritySC }]}
                    isDisabled
                    placeholder={t("sbom.select-sbom-type")}
                  />
                </div>
              </div>

              {/* Remark */}
              <div className="mt-4">
                <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                  {t("sbom.remark")}
                </label>
                <CustomTextarea
                  data-testid="add-sbom-remark-input"
                  rows={4}
                  placeholder={t("sbom.remark-placeholder") || ""}
                  value={formData.remark}
                  onChange={(e) => handleInputChange("remark", e.target.value)}
                  title={formData.remark}
                  maxLength={CHARACTER_LENGTH.REMARK}
                  resize="none"
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="add-sbom-remark-char-count">
                  {(formData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                </p>
              </div>

              <div className="mt-6">
                  <DualSelectBox
                    availableItems={availableItems}
                    selectedItems={selectedItems}
                    onMoveToSelected={handleMoveToSelected}
                    onMoveToAvailable={handleMoveToAvailable}
                    onSelectAllAvailable={handleSelectAllAvailable}
                    onSelectAllSelected={handleSelectAllSelected}
                    selectedAvailableItems={selectedAvailableItems}
                    selectedSelectedItems={selectedSelectedItems}
                    onAvailableSelectionChange={setSelectedAvailableItems}
                    onSelectedSelectionChange={setSelectedSelectedItems}
                    disabled={false}
                    height="450px"
                    canEdit={true}
                  />
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddSbom;
